import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { EDGE_APPS, openApp, SCOPE_EDGE_COMPANIES, SCOPE_EDGE_USERS } from '../config/apps'
import { EdgeBar } from '../components/PortalLayout'

/**
 * 로그인 직후 앱 선택 화면(EDGE 런처). 계정이 접근 가능한 앱(토큰 aud)이 둘 이상일 때만 거치고,
 * 하나면 Login 에서 곧바로 그 앱으로 보낸다. 로그인 후에도 /apps 로 다시 올 수 있다.
 * Admin·Manager 에게는 조직 관리(EDGE 콘솔, /admin) 진입점을 함께 보여준다 — 콘솔은 앱 구독과 무관하게
 * 모든 EDGE 계정이 쓰는 플랫폼 기능이라 앱 카드와 층을 나눠 배치한다.
 */
export default function AppSelect() {
  const navigate = useNavigate()
  const { apps, role, logout, can } = useAuthStore()
  const available = EDGE_APPS.filter(a => apps.includes(a.id))
  const canManage = role === 'Manager' || can(SCOPE_EDGE_USERS) || can(SCOPE_EDGE_COMPANIES)

  const handleLogout = () => { logout(); navigate('/login') }

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <EdgeBar right={<button onClick={handleLogout} className="text-white/65 hover:text-white transition-colors">로그아웃</button>} />

      <main className="flex-1 px-6 py-14">
        <div className="w-full max-w-3xl mx-auto">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">어떤 서비스를 열까요?</h1>
          <p className="text-sm text-gray-500 mt-1.5">회사에서 사용 중인 서비스입니다.</p>

          {available.length === 0 ? (
            <div className="mt-8 bg-surface rounded-xl border border-gray-200 p-8">
              <p className="text-sm text-gray-800 font-medium">사용 중인 서비스가 없습니다.</p>
              <p className="text-sm text-gray-500 mt-1">
                {canManage ? '아래 조직 관리에서 회사의 사용 서비스를 켤 수 있습니다.' : '관리자에게 서비스 사용을 요청하세요.'}
              </p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {available.map(app => (
                <button key={app.id} onClick={() => openApp(app, navigate)}
                  className="group flex items-start gap-4 bg-surface rounded-xl border border-gray-200 p-5 text-left hover:border-gray-400 transition-colors">
                  <div className="w-11 h-11 rounded-lg bg-bar text-white grid place-items-center text-[13px] font-semibold tracking-wider shrink-0">
                    {app.mark}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-gray-900 group-hover:text-accent transition-colors">{app.label}</div>
                    <div className="text-[13px] text-gray-500 mt-0.5 leading-relaxed">{app.description}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {canManage && (
            <div className="mt-12 pt-8 border-t border-gray-300/70">
              <Link to="/admin"
                className="group flex items-center gap-4 rounded-xl px-5 py-4 -mx-5 hover:bg-surface transition-colors">
                {/* 앱 타일(어두운 바탕)과 반전된 타일 — 앱이 아니라 플랫폼 콘솔이라는 표시 */}
                <div className="w-11 h-11 rounded-lg border-2 border-gray-800 text-gray-800 grid place-items-center text-[13px] font-semibold shrink-0">
                  관리
                </div>
                <div className="flex-1">
                  <div className="text-[15px] font-semibold text-gray-900 group-hover:text-accent transition-colors">조직 관리</div>
                  <div className="text-[13px] text-gray-500 mt-0.5">
                    사용자를 초대하고 권한을 정합니다{can(SCOPE_EDGE_COMPANIES) ? '. 회사와 서비스 구독도 여기서 관리합니다.' : '.'}
                  </div>
                </div>
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
